import { api } from "./api";
import { formatPrice, platformLabel } from "./format";

/**
 * Everything the Saved page does that is not JSX.
 *
 * The split is the Phase 3 pattern: logic here where it can be asserted
 * directly, and `pages/Saved.tsx` reduced to rendering plus the three states
 * every data page owes the user. A component test that has to mount a grid to
 * find out whether sorting works tests the grid.
 */

/** Mirrors `SavedItem` in `server/routers/saved.py`. */
export interface SavedItem {
  key: string;
  title: string;
  artist: string;
  platform: string;
  price: number | null;
  type: string;
  size: string;
  color: string;
  url: string;
  image_url: string;
  saved_at: string;
}

/**
 * The API already returns newest-first. This does NOT re-sort.
 *
 * Sorting in both places looks like belt and braces and is worse than either:
 * the two implementations disagree the moment one changes, and whichever runs
 * last silently wins. The server owns the order because it owns the data; the
 * ordering test lives in `test_saved_router.py`.
 */
export async function fetchSaved(): Promise<SavedItem[]> {
  const res = await api.authedFetch("/api/saved");
  if (!res.ok) throw new Error(`Could not load saved items (HTTP ${res.status})`);
  return (await res.json()) as SavedItem[];
}

/**
 * Un-star, by state-file key.
 *
 * `encodeURIComponent` is load-bearing: every key contains a `|`, which is not
 * a legal URL character. Unencoded it is accepted by some stacks and rejected
 * by others, so the request that works in dev 400s behind Hosting.
 *
 * A 404 is surfaced rather than swallowed. It means this client and the server
 * disagree about what is saved -- usually a second tab got there first -- and
 * the page's answer is to reload, not to pretend the click worked.
 */
export async function unsave(key: string): Promise<void> {
  const res = await api.authedFetch(`/api/saved/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
  if (res.status === 404) {
    throw new Error("That item is no longer saved. Reload to see the current list.");
  }
  if (!res.ok) throw new Error(`Could not un-save (HTTP ${res.status})`);
}

/**
 * The metadata line under the title: price, then platform.
 *
 * Built here rather than in JSX so the "no price" case is a tested string
 * rather than a conditional buried in a template.
 */
export function metaLine(item: SavedItem): string {
  const price = formatPrice(item.price);
  const platform = platformLabel(item.platform);
  return [price, platform].filter(Boolean).join(" | ");
}

/** Falls back to the key when an item has no title. */
export function displayTitle(item: SavedItem): string {
  return item.title || item.key;
}
